import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for detecting when the browser is idle
 * 
 * This hook monitors user activity (mouse, keyboard, scroll, touch, focus, visibility)
 * and triggers a callback when the user has been inactive for a specified duration.
 * 
 * @param options - Configuration options for idle detection
 * @param options.idleTime - Time in milliseconds before considering user idle
 * @param options.onIdle - Callback to execute when user becomes idle
 * @param options.enabled - Whether idle detection is enabled (default: true)
 * 
 * @returns Object with resetTimer function and isIdle state
 * 
 * @example
 * ```tsx
 * const { resetTimer, isIdle } = useIdleDetection({
 *   idleTime: 120000, // 2 minutes
 *   onIdle: () => console.log('User is idle'),
 *   enabled: true
 * });
 * ```
 */
interface UseIdleDetectionOptions {
  idleTime: number; // Time in milliseconds before considering user idle
  onIdle: () => void; // Callback to execute when user becomes idle
  enabled?: boolean; // Whether idle detection is enabled
}

export const useIdleDetection = ({
  idleTime,
  onIdle,
  enabled = true
}: UseIdleDetectionOptions) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isIdleRef = useRef(false);

  // Reset the idle timer
  const resetIdleTimer = useCallback(() => {
    if (!enabled) return;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // If we were idle, we're no longer idle
    if (isIdleRef.current) {
      isIdleRef.current = false;
    }

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      if (!isIdleRef.current) {
        isIdleRef.current = true;
        onIdle();
      }
    }, idleTime);
  }, [idleTime, onIdle, enabled]);

  useEffect(() => {
    if (!enabled) return;

    // Events that indicate user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'focus',
      'visibilitychange'
    ];

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, resetIdleTimer, true);
    });

    // Start the initial timer
    resetIdleTimer();

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      events.forEach(event => {
        document.removeEventListener(event, resetIdleTimer, true);
      });
    };
  }, [resetIdleTimer, enabled]);

  // Return function to manually reset the timer
  return {
    resetTimer: resetIdleTimer,
    isIdle: isIdleRef.current
  };
}; 